<template>
  <form
    @submit.prevent="$emit('submit')"
    class="login-form"
    novalidate
  >
    <!-- Email -->
    <form-field
      id="login-email"
      type="text"
      label="Email"
      :model-value="email"
      @update:model-value="$emit('update:email', $event as string)"
      :error-message="errors.email"
      :required="true"
      placeholder="you@firm.com"
      :disabled="loading"
    />

    <!-- Password (eye toggle via FormField) -->
    <form-field
      id="login-pw"
      type="password"
      label="Password"
      :model-value="password"
      @update:model-value="$emit('update:password', $event as string)"
      :error-message="errors.password"
      :required="true"
      placeholder="••••••••"
      :disabled="loading"
    />

    <!-- Remember Me -->
    <form-field
      id="login-remember"
      type="checkbox"
      label="Keep me signed in on this device"
      :model-value="rememberMe"
      :disabled="loading"
      compact
      class="remember-field"
      @update:model-value="$emit('update:remember-me', $event as boolean)"
    />

    <!-- Submit -->
    <s-e-button
      type="submit"
      variant="primary"
      :loading="loading"
      :disabled="loading"
      class="block w-full login-cta"
    >
      {{ loading ? 'Signing in...' : 'Sign in' }}
    </s-e-button>

    <!-- Forgot password — below button -->
    <div class="forgot-row">
      <router-link to="/forgot-password" class="forgot-link">
        Forgot password?
      </router-link>
    </div>

    <!-- Global Form Error -->
    <error-message v-if="errors.form" :message="errors.form" />
  </form>
</template>

<script setup lang="ts">
import FormField from '@/components/ui/FormField.vue'
import SEButton from '@/components/SEButton.vue'
import ErrorMessage from '@/components/ui/ErrorMessage.vue'

defineProps<{
  email: string
  password: string
  rememberMe: boolean
  errors: {
    email?: string
    password?: string
    form?: string
  }
  loading: boolean
}>()

defineEmits<{
  'update:email': [value: string]
  'update:password': [value: string]
  'update:remember-me': [value: boolean]
  submit: []
}>()
</script>

<style scoped>
.login-form {
  display: flex;
  flex-direction: column;
  /* Reduced gap to bring fields closer vertically */
  gap: 0;
}

.login-form :deep(.form-field-wrapper) {
  margin-bottom: 0;
}

.remember-field {
  padding: 4px 0 4px;
}

.remember-field :deep(.v-label) {
  color: var(--text-body);
  font-size: var(--font-size-caption);
  line-height: 1;
}

.login-cta {
  margin-top: 8px;
  --btn-transition: transform 160ms ease, box-shadow 160ms ease, background-color 160ms ease, border-color 160ms ease;
}

.forgot-row {
  /* Left align forgot password and reduce top spacing */
  text-align: left;
  padding-top: 8px;
}

.forgot-link {
  font-size: var(--font-size-caption);
  color: var(--accent);
  text-decoration: none;
  transition: opacity 0.2s ease;
}

.forgot-link:hover { opacity: 0.8; }

.forgot-link:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
  border-radius: var(--radius-xs);
}

</style>
